import * as Api from '@ViewModels';
import equal from 'fast-deep-equal';

/**
 * Whether the account has the Files feature available. The Files feature (the contact Files tab,
 * the "File Request Link" merge field, etc.) depends on Surveys, so both `surveys` and `files`
 * must be enabled. Use this everywhere the Files feature is gated rather than re-deriving the
 * combined check inline.
 */
export const isFilesFeatureEnabled = (account?: Partial<Api.IAccount>): boolean =>
	!!account?.features?.surveys?.enabled && !!account?.features?.files?.enabled;

/**
 * Whether the `{{file request}}` merge field may be offered and persisted. The editor's placeholder
 * plugin strips the token from outgoing content when this is false, so every surface that offers the
 * token must gate on the same answer or the menu will insert something that is silently dropped on save.
 */
export const isFileRequestTokenEnabled = (
	userSession?: Api.UserSessionContext,
	impersonationContext?: Api.IImpersonationContext
): boolean =>
	isFilesFeatureEnabled(impersonationContext?.account || userSession?.account) &&
	!!userSession?.user?.userGroupPermissions?.featurePermissions?.files?.requestFile;

/**
 * Whether the account may run an insurance data refresh: an import that clears renewal keyfacts and
 * policy tags before loading the new data.
 * @param account The account whose preferences are checked.
 */
export const isInsuranceDataRefreshEnabled = (account?: Partial<Api.IAccount>): boolean =>
	!!account?.preferences?.deleteRenewalKeyFacts;

/**
 * Gets user from ownership filter
 * @source Original location: UiUtils.ts.backup line 2834
 */
export const getUserFromOwnershipFilter = (filter?: Api.IOwnershipFilter) => {
	return filter?.criteria?.property === Api.ContactFilterCriteriaProperty.OwnedBy ? filter.criteria.value : null;
};

/**
 * Checks if ownership filters are equal
 * @source Original location: UiUtils.ts.backup line 2838
 */
export const areOwnershipFiltersEqual = (
	currentUserId: string,
	a: Api.IOwnershipFilter | null,
	b: Api.IOwnershipFilter | null
) => {
	if (!a && !b) {
		return true;
	}

	if (!a || !b) {
		return false;
	}

	const isOwnedBy =
		a.criteria.property === Api.ContactFilterCriteriaProperty.OwnedBy &&
		b.criteria.property === Api.ContactFilterCriteriaProperty.OwnedBy;

	const isMyConnections =
		a.criteria.property === Api.ContactFilterCriteriaProperty.Connections &&
		b.criteria.property === Api.ContactFilterCriteriaProperty.Connections;

	if (isOwnedBy || isMyConnections) {
		if (a.criteria.values || b.criteria.values) {
			return equal(a.criteria.values, b.criteria.values);
		}

		// no value and current user id are equivalent
		if (
			(!a.criteria.value || a.criteria.value.length === 0 || a.criteria.value === currentUserId) &&
			(!b.criteria.value || b.criteria.value.length === 0 || b.criteria.value === currentUserId)
		) {
			return true;
		}

		return a.criteria.value === b.criteria.value;
	}

	return equal(a.criteria, b.criteria);
};

/**
 * Checks if ownership dropdown should be disabled
 * @source Original location: UiUtils.ts.backup line 2872
 */
export const shouldDisableOwnershipDropdown = (disable: boolean, sendFrom?: Api.ISendOnBehalfPermission) => {
	if (disable || sendFrom?.requiredFilter) {
		return true;
	}

	return sendFrom?.sender && sendFrom.sender.mode === Api.SendEmailFrom.ContactOwner;
};
